import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { AxiosRequestHeaders } from 'axios';
import { CustomReportDataQueryParams, CustomReportMetadata, ReportMetadata } from '@urc-ui/store/api/report-metadata/types';

export const resolveAdvertisingApiBaseUrl = () => {
  const appContext = window.AppContext;
  const defaultApiUrl = 'https://advertising.integ.amazon.com/a9g-api-gateway';
  const path = (appContext?.advertisingApiEndpoint as string) || '';

  // if path is falsy, OR if the current origin contains a port number (i.e. presumed to be served from local dev server), return default
  if (!path || window.location.port) {
    return defaultApiUrl;
  }

  const origin = window.location.origin.includes('advertising-preprod')
    ? window.location.origin.replace('advertising-preprod', 'advertising')
    : window.location.origin;

  // if path is truthy but is not a complete URL (i.e. does not start with protocol 'http'), prepend current origin
  if (!path.startsWith('http')) {
    return `${origin}${path}`;
  }
  return path;
};

const getAdvertisingApiHeaders = () => {
  const appContext = window.AppContext;
  return {
    'amazon-advertising-api-advertiserid': appContext.entityId as string,
    'amazon-advertising-api-clientid': appContext.advertisingApiClientId as string,
    'amazon-advertising-api-csrf-data': appContext.advertisingApiClientId as string,
    'amazon-advertising-api-marketplaceid': appContext.marketplaceId as string,
    'amazon-advertising-api-csrf-token': appContext.advertisingApiCsrfToken as string,
    'x-use-obfuscated-ids': true as boolean,
    'amazon-advertising-api-isimpersonator': (appContext.isSpoofer === 'true') as boolean,
    'amazon-advertising-api-isportalserver': window.isMidwayAuthenticated as boolean
  };
};

const getReportBuilderApiHeaders = () => {
  const appContext = window.AppContext;
  return {
    advertiserId: appContext.advertiserId as string,
    entityId: appContext.entityId as string,
    entityType: appContext.advertiserType as string,
    locale: (appContext.locale as string)?.replace('_', ''),
    marketplaceId: appContext.decryptedMarketplaceId as string,
    isGlobal: !!appContext.globalAccountId as boolean,
    isPublisher: (appContext.advertiserType === 'BOOKVENDOR') as boolean
  };
};

const getHeaders = (): AxiosRequestHeaders => {
  return {
    ...getAdvertisingApiHeaders(),
    ...getReportBuilderApiHeaders()
  };
};

export const reportMetadataApi = createApi({
  reducerPath: 'reportMetadataApi',
  baseQuery: axiosBaseQuery({
    baseUrl: resolveAdvertisingApiBaseUrl()
  }),
  tagTypes: [Tag.REPORT_METADATA],
  endpoints: builder => ({
    getReportMetadata: builder.query<ReportMetadata, void>({
      query: () => {
        return { url: '/reportMetadata', method: HttpMethod.GET, headers: getHeaders() };
      },
      // eslint-disable-next-line local-rules/sim-ticket-link
      // TODO: Transform data as needed on create report page.
      transformResponse: (response: ReportMetadata) => response,
      providesTags: [Tag.REPORT_METADATA]
    }),
    getCustomReportMetadata: builder.query<CustomReportMetadata, CustomReportDataQueryParams>({
      query: (customReportDataQueryParams: CustomReportDataQueryParams) => {
        return {
          url: `/reportMetadata/custom/${customReportDataQueryParams.reportCategory}/${customReportDataQueryParams.reportType}`,
          method: HttpMethod.GET,
          headers: getHeaders()
        };
      },
      // TODO: Transform data as needed on create report page. https://issues.amazon.com/issues/RCMRX-6661
      transformResponse: (response: CustomReportMetadata) => response
    })
  })
});

export const selectReportMetadataResult = reportMetadataApi.endpoints.getReportMetadata.select();
export const selectCustomReportMetadataResult = (queryParams: CustomReportDataQueryParams) =>
  reportMetadataApi.endpoints.getCustomReportMetadata.select(queryParams);

export const { useGetReportMetadataQuery, useGetCustomReportMetadataQuery } = reportMetadataApi;
